import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
} from '@primer/octicons-react'
import {ActionList, Link} from '@primer/react'

import type {PullRequestNode} from './closed-by-pull-requests-references-list-query-types'
import styles from './PullRequest.module.css'

// `repository` is narrowed to the plain `{name, owner: {login}}` shape actually read here, so both the
// Relay node and the server-persisted query node satisfy it.
export type PullRequestFragment = Pick<
  PullRequestNode,
  'id' | 'title' | 'number' | 'merged' | 'closed' | 'isDraft' | 'url'
> & {
  readonly repository: {
    readonly name: string
    readonly owner: {readonly login: string}
  }
}

type Props = {
  pullRequest: PullRequestFragment
  hovercard?: boolean
}

export function PullRequest({pullRequest, hovercard}: Props) {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const {
    id,
    number,
    title,
    repository: {owner, name},
    url,
    merged,
    closed,
    isDraft,
  } = pullRequest
  const {login} = owner

  const getIcon = () => {
    if (merged) {
      return <GitMergeIcon size={16} className={styles.mergedIcon} />
    } else if (closed) {
      return <GitPullRequestClosedIcon size={16} className={styles.closedIcon} />
    } else if (isDraft) {
      return <GitPullRequestDraftIcon size={16} className={styles.draftIcon} />
    } else {
      return <GitPullRequestIcon size={16} className={styles.openIcon} />
    }
  }

  if (hovercard) {
    return (
      <Link
        href={url}
        target="_blank"
        {...getHovercardAttributesForType('pull_request', {
          owner: login,
          repo: name,
          pullRequestNumber: number,
        })}
        aria-label={`link to the pull request #${title}`}
        className={styles.hovercardLink}
      >
        <GitPullRequestIcon size={16} />
        <span className={styles.countText}>1</span>
      </Link>
    )
  }

  return (
    <ActionList.Item key={id}>
      <ActionList.LeadingVisual>{getIcon()}</ActionList.LeadingVisual>
      <a href={url} aria-label={`link to the issue #${title}`} className={styles.pullRequestItemContent}>
        <span>{title}</span>
        <span className={styles.repositoryInfo}>{`${name}#${number}`}</span>
      </a>
    </ActionList.Item>
  )
}
